import { useEffect } from 'react'
import {
  Navigate,
  Outlet,
  useLocation,
  useSearchParams,
} from 'react-router-dom'
import type { ABTestConfig } from '../contexts/ABTestContext'
import { useABTestVariant } from '../contexts/ABTestContext'
import { getInitialRedirectPath } from '../helpers/abTestRouting'
import { setABTestVariantInContext } from '../helpers/variantContext'
import LoadingPage from '../pages/LoadingPage'
import type { ABTestRouteConfig } from '../types/abtest'

interface ABTestSectionOutletProps {
  /**
   * The base path for this section (e.g., '/onboarding', '/checkout')
   */
  basePath: string
  /**
   * The AB test route configuration for this section
   */
  routeConfig: ABTestRouteConfig
  /**
   * Function to get the AB test config (returns null if AB testing is disabled)
   */
  getABTestConfig: () => ABTestConfig | null
  /**
   * Map of variant names to their URL path prefixes
   * e.g., { v1: 'v1', v2: 'v2' }
   * Baseline variant uses no prefix by default
   */
  variantPathPrefixes?: Record<string, string>
  /**
   * List of valid variant names for URL parameter override
   * Defaults to ['baseline', 'v1']
   */
  validVariants?: string[]
}

/**
 * Generic A/B test section outlet that redirects to the correct first page based on variant.
 * Works for any section (onboarding, checkout, etc.) by parameterizing the base path.
 * Supports URL parameter override for testing: /{basePath}?variant=v1
 *
 * @example
 * // For onboarding:
 * <ABTestSectionOutlet
 *   basePath="/onboarding"
 *   routeConfig={onboardingRouteConfig}
 *   getABTestConfig={getABTestConfig}
 *   variantPathPrefixes={{ v1: 'v1' }}
 * />
 *
 * @example
 * // For checkout:
 * <ABTestSectionOutlet
 *   basePath="/checkout"
 *   routeConfig={checkoutRouteConfig}
 *   getABTestConfig={getABTestConfig}
 *   variantPathPrefixes={{ v1: 'v1' }}
 * />
 */
export default function ABTestSectionOutlet({
  basePath,
  routeConfig,
  getABTestConfig,
  variantPathPrefixes = {},
  validVariants = ['baseline', 'v1'],
}: ABTestSectionOutletProps) {
  const abTestConfig = getABTestConfig()
  const location = useLocation()

  const initialPath = getInitialRedirectPath('baseline', routeConfig)

  // If A/B testing is not configured, just use the default baseline redirect
  if (!abTestConfig) {
    if (
      location.pathname === basePath ||
      location.pathname === `${basePath}/`
    ) {
      return <Navigate to={`${initialPath}${window.location.search}`} replace />
    }
    return <Outlet />
  }

  // Otherwise, use the variant-based redirect
  return (
    <ABTestSectionOutletWithABTest
      basePath={basePath}
      routeConfig={routeConfig}
      variantPathPrefixes={variantPathPrefixes}
      validVariants={validVariants}
    />
  )
}

interface ABTestSectionOutletWithABTestProps {
  basePath: string
  routeConfig: ABTestRouteConfig
  variantPathPrefixes: Record<string, string>
  validVariants: string[]
}

function ABTestSectionOutletWithABTest({
  basePath,
  routeConfig,
  variantPathPrefixes,
  validVariants,
}: ABTestSectionOutletWithABTestProps) {
  const { variant, setVariant, isLoading } = useABTestVariant()
  const location = useLocation()
  const [searchParams] = useSearchParams()

  // Override variant if URL param "variant" is present
  // This is useful for debugging and testing different variants
  // Usage: /{basePath}?variant=v1 or /{basePath}?variant=baseline
  const variantParam = searchParams.get('variant')

  // Check if user is on a baseline path (no variant prefix in URL)
  const basePathRegex = new RegExp(`^${basePath.replace(/\//g, '\\/')}/?`)
  const pathAfterSection = location.pathname.replace(basePathRegex, '')
  const currentPathPrefix = pathAfterSection.split('/')[0]
  const variantPrefixValues = Object.values(variantPathPrefixes)
  const isOnVariantPath = variantPrefixValues.includes(currentPathPrefix)

  const variantFromPath = isOnVariantPath
    ? Object.entries(variantPathPrefixes).find(
        ([, prefix]) => prefix === currentPathPrefix,
      )?.[0]
    : null

  useEffect(() => {
    if (variantParam && validVariants.includes(variantParam)) {
      if (variant !== variantParam) {
        setVariant(variantParam as 'baseline' | 'v1' | 'v2')
        setABTestVariantInContext(variantParam)
      }
    } else if (
      variantFromPath &&
      validVariants.includes(variantFromPath) &&
      variant !== variantFromPath
    ) {
      setVariant(variantFromPath as 'baseline' | 'v1' | 'v2')
      setABTestVariantInContext(variantFromPath)
    }
  }, [variantParam, variantFromPath, variant, setVariant, validVariants])

  if (isLoading) {
    return <LoadingPage />
  }

  const isSectionRoot =
    location.pathname === basePath || location.pathname === `${basePath}/`

  const isOnVariantRoot =
    isOnVariantPath &&
    (pathAfterSection === currentPathPrefix ||
      pathAfterSection === `${currentPathPrefix}/`)

  const isOnBaselinePath = !isOnVariantPath

  const expectedPrefix = variantPathPrefixes[variant]

  // Check if the current path is a known A/B tested page (exists in the
  // route config) rather than a shared route like 'payment' or
  // 'payment-completion' that lives outside variant directories.
  const isKnownABTestedPage = routeConfig.baseline.some(
    (pagePath) =>
      pathAfterSection === pagePath ||
      pathAfterSection.startsWith(`${pagePath}/`),
  )

  const needsVariantRedirect =
    variant !== 'baseline' &&
    isOnBaselinePath &&
    !isSectionRoot &&
    expectedPrefix &&
    isKnownABTestedPage

  if (isSectionRoot) {
    const initialPath = getInitialRedirectPath(variant, routeConfig)
    let redirectPath = initialPath
    if (expectedPrefix) {
      redirectPath = `${expectedPrefix}/${initialPath}`
    }
    return <Navigate to={`${redirectPath}${window.location.search}`} replace />
  }

  if (isOnVariantRoot && variantFromPath) {
    const initialPath = getInitialRedirectPath(variantFromPath, routeConfig)
    const redirectPath = `${currentPathPrefix}/${initialPath}`
    return <Navigate to={`${redirectPath}${window.location.search}`} replace />
  }

  if (needsVariantRedirect) {
    // User landed on a baseline path (e.g., /checkout/plans from a direct link)
    // but their resolved variant is not baseline (e.g., v1 or v2)
    // Redirect them to the same page under their variant prefix
    // e.g., /checkout/plans -> /checkout/v1/plans
    const redirectPath = `${expectedPrefix}/${pathAfterSection}`
    return <Navigate to={`${redirectPath}${window.location.search}`} replace />
  }

  return <Outlet />
}
